import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldBan, ShieldCheck, Loader2, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

// Admin switch: bar / lift "wallet as payment source" per account.
export function AdminWalletRestrictions({ api }) {
  const [rows, setRows] = useState([]);
  const [defaultReason, setDefaultReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wallet/restrictions");
      setRows(data.rows || []); setDefaultReason(data.default_reason || "");
    } catch { toast.error("Could not load wallet restrictions"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const lift = async (r) => {
    if (!window.confirm(`Lift the wallet-payment restriction for ${r.email}? They will be able to pay from their wallet immediately.`)) return;
    setBusy(r.email);
    try { await api.delete(`/admin/wallet/restrictions/${encodeURIComponent(r.email)}`); toast.success(`Restriction lifted · ${r.email}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not lift restriction"); }
    finally { setBusy(null); }
  };
  const impose = async (target, why) => {
    setBusy(target);
    try {
      await api.post("/admin/wallet/restrictions", { email: target, reason: (why || defaultReason).trim() });
      toast.success(`Wallet payments restricted · ${target}`); setEmail(""); setReason(""); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not impose restriction"); }
    finally { setBusy(null); }
  };

  const active = rows.filter((r) => r.active); const lifted = rows.filter((r) => !r.active);
  return (
    <div className="admin-card" data-testid="admin-wallet-restrictions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#C9A84C] inline-flex items-center gap-2"><ShieldBan size={14} /> Payment-source restrictions</div>
          <h3 className="font-serif text-2xl text-[#F5F5F5] mt-1">Wallet payment switch</h3>
          <p className="text-sm text-[#888888] mt-1 max-w-2xl">Accounts listed here cannot use their Vinci Wallet to pay invoices or make VCB transfers until Vinci Inc lifts the restriction. Top-ups and statements stay available.</p>
        </div>
        <Button data-testid="restriction-new" onClick={() => setShowForm((v) => !v)} className="bg-[#C9A84C] hover:bg-[#D8B75B] text-black text-sm font-semibold"><Plus size={14} className="mr-1" /> Restrict an account</Button>
      </div>
      {showForm && (
        <div className="mt-5 grid gap-3 md:grid-cols-[1fr_2fr_auto] items-end rounded-lg border border-[#262626] bg-[#0D0D0D] p-4" data-testid="restriction-form">
          <div><label className="text-sm text-[#C9A84C]">Account email</label><Input data-testid="restriction-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@company.com" className="bg-black/40 border-[#333333] mt-1 text-sm" /></div>
          <div><label className="text-sm text-[#C9A84C]">Reason shown to the partner</label><Textarea data-testid="restriction-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={defaultReason} className="bg-black/40 border-[#333333] mt-1 text-sm" /></div>
          <Button data-testid="restriction-impose" disabled={!email.includes("@") || busy === email} onClick={() => impose(email.trim().toLowerCase(), reason)} className="bg-[#C0392B] hover:bg-[#DE4B3C] text-white text-sm font-semibold">Impose</Button>
        </div>
      )}
      {loading ? <div className="p-8 text-center text-[#888888]"><Loader2 className="animate-spin inline" /></div> : (
        <div className="mt-6 space-y-3">
          {active.length === 0 && <div className="text-sm text-[#888888] italic">No active restrictions.</div>}
          {active.map((r) => (
            <div key={r.email} data-testid={`restriction-row-${r.email}`} className="rounded-lg border border-[#C0392B]/40 bg-[#C0392B]/5 p-5 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-[260px]">
                <div className="text-base font-bold text-[#F5F5F5]">{r.business_name || r.name || r.email}</div>
                <div className="text-sm text-[#888888]">{r.email}{r.name ? ` · ${r.name}` : ""}</div>
                <div className="text-sm text-[#D4D4D4] mt-2 italic">"{r.reason}"</div>
                <div className="text-sm text-[#888888] mt-1">Imposed {r.imposed_at ? new Date(r.imposed_at).toLocaleString() : "—"} · {r.imposed_by}</div>
              </div>
              <Button data-testid={`restriction-lift-${r.email}`} disabled={busy === r.email} onClick={() => lift(r)} className="bg-[#2FB999] hover:bg-[#3FD9B0] text-black text-sm font-semibold">
                <ShieldCheck size={14} className="mr-1" /> Lift restriction
              </Button>
            </div>
          ))}
          {lifted.length > 0 && (
            <details className="mt-4">
              <summary className="text-sm text-[#888888] cursor-pointer">Previously lifted ({lifted.length})</summary>
              <div className="mt-3 space-y-2">
                {lifted.map((r) => (
                  <div key={r.email} data-testid={`restriction-lifted-${r.email}`} className="rounded-lg border border-[#262626] p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div><span className="text-[#F5F5F5]">{r.business_name || r.email}</span> <span className="text-[#888888]">· {r.email} · lifted {r.lifted_at ? new Date(r.lifted_at).toLocaleString() : "—"} by {r.lifted_by}</span></div>
                    <Button data-testid={`restriction-reimpose-${r.email}`} variant="outline" disabled={busy === r.email} onClick={() => impose(r.email, r.reason)} className="border-[#333333] text-[#D4D4D4] hover:border-[#C0392B] text-sm">Re-impose</Button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
